import { c as _c } from "react/compiler-runtime";
import { type AutocompleteChangeDetails, type AutocompleteChangeReason, type AutocompleteRenderGetTagProps, type AutocompleteRenderGroupParams, type AutocompleteRenderOptionState, type AutocompleteValue, type FilterOptionsState, Autocomplete as MUIAutocomplete, type PopperProps, createFilterOptions } from '@mui/material';
import { type ClipboardEvent, type ClipboardEventHandler, type FocusEvent, type FocusEventHandler, type HTMLAttributes, type MouseEvent, type ReactElement, type ReactNode, type SyntheticEvent, forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import Checkbox from '../Checkbox';
import Chip from '../Chip';
import Divider from '../Divider';
import DotsLoader from '../DotsLoader';
import DropdownItem from '../Dropdown/DropdownItem';
import DropdownList from '../Dropdown/DropdownList';
import DropdownListSubCategory from '../Dropdown/DropdownListSubCategory';
import Grid from '../Grid';
import { ChevronDownIcon } from '../icons/ChevronDownIcon';
import Radio from '../Radio/Radio';
import Typography from '../Typography';
import { AUTOCOMPLETE_POPPER_CLASS_NAME } from './classNames';
import { StyledInputAutocompleteTextField, StyledPopper, StyledTypographyNoOptionsText } from './styles';
import { type AutocompleteOptionItem, type AutocompleteProps, type ListboxComponentProps, type RenderInputParams, type RenderOptionProps, type SaveCustomValueTextComponentProps } from './types';
const renderInput = (params: RenderInputParams, color?: 'error' | 'success', helperText?: string | ReactElement, onPaste?: ClipboardEventHandler<HTMLInputElement>, onBlur?: FocusEventHandler<HTMLInputElement>): ReactNode => {
  const {
    inputElement,
    multiple,
    ...textFieldParams
  } = params;
  return <Grid display="flex" flexDirection="column" gap="8px">
      <StyledInputAutocompleteTextField {...textFieldParams} value={!multiple ? inputElement?.value?.toString() ?? '' : undefined} color={color} $assistive={false} $isRightIconClickable={false} $hasLeftIcon={false} $hasRightIcon={false} inputProps={{
      ...params.inputProps,
      onPaste: e => {
        params.inputProps?.onPaste?.(e as unknown as ClipboardEvent<HTMLInputElement>);
        onPaste?.(e as unknown as ClipboardEvent<HTMLInputElement>);
      },
      onBlur: e => {
        params.inputProps?.onBlur?.(e as unknown as FocusEvent<HTMLInputElement>);
        onBlur?.(e as unknown as FocusEvent<HTMLInputElement>);
      }
    }} />
      {helperText !== undefined && <Typography color={color} variant="body3">
          {helperText}
        </Typography>}
    </Grid>;
};
const createPopperComponent = (customZIndex?: number) => {
  return (props: PopperProps): ReactElement => {
    const style = customZIndex === undefined ? props.style : {
      ...props.style,
      zIndex: customZIndex
    };
    return <StyledPopper {...props} className={[AUTOCOMPLETE_POPPER_CLASS_NAME, props.className].filter(Boolean).join(' ')} style={style} onMouseDown={event => {
      event.preventDefault();
    }}>
        {props.children}
      </StyledPopper>;
  };
};
const SaveCustomValueTextComponent = t0 => {
  const $ = _c(26);
  const {
    acceptCustomInputTemplate,
    inputValue
  } = t0;
  let T0;
  let T1;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[0] !== acceptCustomInputTemplate || $[1] !== inputValue) {
    const [firstFragment, secondFragment] = acceptCustomInputTemplate.split("{chipKey}");
    T1 = Grid;
    t3 = "flex";
    t4 = "nowrap";
    t5 = "center";
    t6 = "4px";
    t7 = <Typography variant="h5">{firstFragment.replace("{value}", inputValue)}</Typography>;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <Chip label="Enter" />;
      $[12] = t8;
    } else {
      t8 = $[12];
    }
    T0 = Typography;
    t1 = "h5";
    t2 = secondFragment.replace("{value}", inputValue);
    $[0] = acceptCustomInputTemplate;
    $[1] = inputValue;
    $[2] = T0;
    $[3] = T1;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
    $[11] = t8;
  } else {
    T0 = $[2];
    T1 = $[3];
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
    t4 = $[7];
    t5 = $[8];
    t6 = $[9];
    t7 = $[10];
    t8 = $[11];
  }
  let t9;
  if ($[13] !== T0 || $[14] !== t1 || $[15] !== t2) {
    t9 = <T0 variant={t1}>{t2}</T0>;
    $[13] = T0;
    $[14] = t1;
    $[15] = t2;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== T1 || $[18] !== t3 || $[19] !== t4 || $[20] !== t5 || $[21] !== t6 || $[22] !== t7 || $[23] !== t8 || $[24] !== t9) {
    t10 = <T1 display={t3} flexWrap={t4} alignItems={t5} gap={t6}>{t7}{t8}{t9}</T1>;
    $[17] = T1;
    $[18] = t3;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  return t10;
};
const ListboxComponent = forwardRef<HTMLUListElement, ListboxComponentProps>((props, ref) => {
  const $ = _c(15);
  const shouldRenderSaveCustomValueText = props.allowCustomInput && props.inputElement?.value != null && props.inputElement?.value !== "" && !props.hasExactMatch && !props.elementIsAlreadyInValue;
  const showSelectAll = props.allowSelectAll === true && props.numFilteredOptions > 0;
  const t0 = props["aria-labelledby"];
  const t1 = showSelectAll ? props.selectAllLabel : undefined;
  const t2 = showSelectAll ? props.selectAllValue : undefined;
  const t3 = showSelectAll ? props.onSelectAllChange : undefined;
  let t4;
  if ($[0] !== props.acceptCustomInputTemplate || $[1] !== props.inputElement?.value || $[2] !== props.numFilteredOptions || $[3] !== shouldRenderSaveCustomValueText) {
    t4 = shouldRenderSaveCustomValueText && <>{props.numFilteredOptions > 0 ? <Divider /> : <Grid height="4px" />}<SaveCustomValueTextComponent inputValue={props.inputElement?.value as string} acceptCustomInputTemplate={props.acceptCustomInputTemplate} /></>;
    $[0] = props.acceptCustomInputTemplate;
    $[1] = props.inputElement?.value;
    $[2] = props.numFilteredOptions;
    $[3] = shouldRenderSaveCustomValueText;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== props.children || $[6] !== props.id || $[7] !== props.role || $[8] !== ref || $[9] !== t0 || $[10] !== t1 || $[11] !== t2 || $[12] !== t3 || $[13] !== t4) {
    t5 = <DropdownList dense={true} role={props.role} id={props.id} aria-labelledby={t0} maxWidth="100%" ref={ref} headerControlTitle={t1} headerControlValue={t2} headerControlOnChange={t3}>{props.children}{t4}</DropdownList>;
    $[5] = props.children;
    $[6] = props.id;
    $[7] = props.role;
    $[8] = ref;
    $[9] = t0;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
});
const renderGroup = (params: AutocompleteRenderGroupParams): ReactElement => {
  if (params.group === '') return <>{params.children}</>;
  return <DropdownListSubCategory header={params.group}>{params.children}</DropdownListSubCategory>;
};
const renderOption = (props: RenderOptionProps, option: AutocompleteOptionItem, state: AutocompleteRenderOptionState, noOptionsTextTemplate: string, inputValue: string, multiple: boolean, indeterminateValues: ReadonlySet<string>): ReactElement => {
  const onClick = props.onClick ?? (() => {});
  if (option.label === 'CUSTOM_INPUT_OTHER_OPTIONS') return <></>;
  if (option.label === 'CUSTOM_INPUT_NO_OPTIONS') return <StyledTypographyNoOptionsText variant="h5">
        {noOptionsTextTemplate.replace('{value}', inputValue)}
      </StyledTypographyNoOptionsText>;
  const isIndeterminate = multiple && indeterminateValues.has(option.value);
  return <DropdownItem tabIndex={props.tabIndex} id={props.id} data-option-index={props['data-option-index']} aria-disabled={option.disabled} aria-selected={props['aria-selected']} role={props.role} onClick={onClick} key={option.value} annotation={option.annotation} icon={option.icon} disabled={option.disabled} control={multiple ? <Checkbox checked={state.selected && !isIndeterminate} indeterminate={isIndeterminate} /> : <Radio checked={state.selected} />} annotationLineType={option.annotationLineType} tooltipProps={option.tooltipProps} href={option.href} LinkComponent={option.LinkComponent} target={option.target} rel={option.rel}>
      {option.customLabelComponent ?? option.label}
    </DropdownItem>;
};
const defaultRenderTags = (value: AutocompleteOptionItem[], getTagProps: AutocompleteRenderGetTagProps): ReactElement => {
  return <>
      {value.map((option, index) => {
      const tagProps = getTagProps({
        index
      });
      return <Chip label={option.label} avatar={option.icon} {...tagProps} key={tagProps.key} />;
    })}
    </>;
};
const defaultIsOptionEqualToValue = <T extends AutocompleteOptionItem,>(option: T, value: string): boolean => option.value === value;
const Autocomplete = <T extends AutocompleteOptionItem, Multiple extends boolean, AllowCustomInput extends boolean>({
  options,
  value,
  onChange,
  multiple,
  allowCustomInput,
  fullWidth = false,
  placeholder = 'Add item...',
  noOptionsTextTemplate = 'Sorry, we couldn\'t find anything with the name "{value}".',
  acceptCustomInputTemplate = 'Press {chipKey} to use "{value}" as item.',
  customInputIcon,
  isOptionEqualToValue = defaultIsOptionEqualToValue,
  dropdownPlacement,
  popperZIndex,
  color,
  helperText,
  isLoadingOptions = false,
  loadingText = 'Loading options...',
  disabled = false,
  renderTags,
  indeterminateValues = [],
  allowSelectAll = false,
  selectAllLabel = 'Select all',
  onPaste,
  onBlur
}: AutocompleteProps<T, Multiple, AllowCustomInput>): ReactElement => {
  const autocompleteRef = useRef<HTMLDivElement>(null);
  const [localInputElement, setLocalInputElement] = useState<HTMLInputElement | null>(null);
  const indeterminateValueSet = new Set(indeterminateValues);
  useEffect(() => {
    if (autocompleteRef.current !== null) {
      setLocalInputElement(autocompleteRef.current?.querySelector('input'));
    }
  }, [autocompleteRef.current]);
  const selectableOptions = options.filter(option => option.disabled !== true);
  const selectAllValue: 'checked' | 'indeterminate' | 'unchecked' = (() => {
    if (!Array.isArray(value) || selectableOptions.length === 0) {
      return 'unchecked';
    }
    const selectedCount = selectableOptions.filter(option_0 => value.some(selected => selected.value === option_0.value)).length;
    const hasIndeterminateSelection = selectableOptions.some(option_1 => indeterminateValueSet.has(option_1.value));
    if (selectedCount === 0) {
      return 'unchecked';
    }
    // Fully checked only when every option is selected and none are partial/mixed.
    if (selectedCount === selectableOptions.length && !hasIndeterminateSelection) {
      return 'checked';
    }
    return 'indeterminate';
  })();
  const handleSelectAllChange = useCallback((event: MouseEvent<HTMLLIElement>, currentlyChecked: boolean): void => {
    if (!multiple || !Array.isArray(value)) {
      return;
    }
    const selectable = options.filter(option_2 => option_2.disabled !== true);
    if (currentlyChecked) {
      const lockedSelected = value.filter(selected_0 => {
        const matchingOption = options.find(option_3 => option_3.value === selected_0.value);
        return matchingOption?.disabled === true;
      });
      onChange(event, lockedSelected as Multiple extends true ? T[] : T | null, 'removeOption');
      return;
    }
    onChange(event, selectable as Multiple extends true ? T[] : T | null, 'selectOption');
  }, [multiple, value, options, onChange]);
  const handleChange = useCallback((event_0: SyntheticEvent, newValue: AutocompleteValue<T, Multiple, true, AllowCustomInput>, reason: AutocompleteChangeReason, details?: AutocompleteChangeDetails<T>): void => {
    const isEnterInput = event_0.nativeEvent.type === 'keydown' && (event_0.nativeEvent as KeyboardEvent)?.key === 'Enter';
    if (Array.isArray(newValue)) {
      if (reason === 'removeOption' && details?.option.label === 'CUSTOM_INPUT_NO_OPTIONS') {
        // Due to an internal bug in MUI, this needs to handle the special case where there's only 1 chip in the value
        // and the user inputs the exact same value as that 1 chip. For some reason that comes through as a "removeOption", but
        // it should simply be treated as any other "duplicate" value input in freesolo.
        onChange(event_0, value, reason, details);
        if (localInputElement != null) localInputElement.value = '';
        return;
      }
      const parsedValue = newValue.map(rowValue => {
        if (typeof rowValue === 'string') {
          const optionToCreateIfNoMatch = {
            value: rowValue,
            label: rowValue,
            icon: customInputIcon
          };
          const matchingOption_0 = options.find(option_4 => isOptionEqualToValue(option_4, rowValue));
          return matchingOption_0 ?? optionToCreateIfNoMatch as AutocompleteOptionItem;
        }
        return rowValue;
      });
      const uniqueValues = new Set();
      const filteredValues = parsedValue.filter(rowValue_0 => {
        if (uniqueValues.has(rowValue_0.value)) {
          return false;
        } else {
          uniqueValues.add(rowValue_0.value);
          return true;
        }
      });
      onChange(event_0, filteredValues as Multiple extends true ? T[] : T | null, reason, details);
      return;
    }
    const isNotMultipleInputValue = typeof newValue === 'string';
    if (isNotMultipleInputValue || isEnterInput && allowCustomInput) {
      const matchingOption_1 = isNotMultipleInputValue ? options.find(option_5 => isOptionEqualToValue(option_5, newValue)) : null;
      if (matchingOption_1 == null && isEnterInput && allowCustomInput) {
        const currentInputValue = autocompleteRef.current?.querySelector('input')?.value;
        const optionToCreateIfNoMatch_0 = {
          value: currentInputValue,
          label: currentInputValue,
          icon: customInputIcon
        };
        onChange(event_0, optionToCreateIfNoMatch_0 as AutocompleteOptionItem as Multiple extends true ? T[] : T | null, reason, details);
        return;
      }
      if (matchingOption_1?.disabled === true) return; // don't allow selecting disabled options

      onChange(event_0, matchingOption_1 as Multiple extends true ? T[] : T | null, reason, details);
    } else {
      onChange(event_0, newValue as Multiple extends true ? T[] : T | null, reason, details);
    }
  }, [customInputIcon, value]);
  const filter = createFilterOptions<T>();
  const showSelectAll = Boolean(multiple) && allowSelectAll && selectableOptions.length > 0;
  const ListBoxComponentWithInputValue = useCallback(forwardRef<HTMLUListElement, HTMLAttributes<HTMLElement>>((props, ref): ReactElement => {
    const filteredOptions = filter(options, {
      inputValue: localInputElement?.value ?? '',
      getOptionLabel: option_6 => option_6.label
    });
    const hasExactMatch = filteredOptions.some(option_7 => isOptionEqualToValue(option_7, localInputElement?.value ?? ''));
    const elementIsAlreadyInValue = Array.isArray(value) ? value?.some(option_8 => isOptionEqualToValue(option_8, localInputElement?.value ?? '')) : false;
    return <ListboxComponent {...props} ref={ref} inputElement={localInputElement} allowCustomInput={allowCustomInput} numFilteredOptions={filteredOptions.length} hasExactMatch={hasExactMatch} acceptCustomInputTemplate={acceptCustomInputTemplate} elementIsAlreadyInValue={elementIsAlreadyInValue} {...showSelectAll ? {
      allowSelectAll: true as const,
      selectAllLabel,
      selectAllValue,
      onSelectAllChange: handleSelectAllChange
    } : {
      allowSelectAll: false as const
    }} />;
  }), [localInputElement, allowCustomInput, acceptCustomInputTemplate, value, showSelectAll, selectAllLabel, selectAllValue, handleSelectAllChange, options, isOptionEqualToValue]);
  const customFilterOptions = useCallback((options_0: T[], params: FilterOptionsState<T>): T[] => {
    const filteredOptions_0 = filter(options_0, params);
    const {
      inputValue
    } = params;
    if (options_0.length === 0) {
      return filteredOptions_0;
    }
    const isExisting = options_0.some(option_9 => isOptionEqualToValue(option_9, inputValue));
    if (inputValue !== '' && !isExisting) {
      const customInputOption = {
        value: inputValue,
        label: filteredOptions_0.length === 0 ? 'CUSTOM_INPUT_NO_OPTIONS' : 'CUSTOM_INPUT_OTHER_OPTIONS',
        icon: customInputIcon
      };
      filteredOptions_0.push(customInputOption as T);
    }
    return filteredOptions_0;
  }, [customInputIcon]);

  // FreeSolo with an empty options list has nothing useful to show in the popup.
  const hideOptionsPopup = allowCustomInput && options.length === 0 && !isLoadingOptions;
  return <MUIAutocomplete<T, Multiple, boolean, AllowCustomInput> key={hideOptionsPopup ? 'hide-options-popup' : 'show-options-popup'} value={value} freeSolo={allowCustomInput} openOnFocus={!allowCustomInput} isOptionEqualToValue={(option_10, value_0) => {
    return option_10.value === value_0.value;
  }} multiple={multiple} fullWidth={fullWidth} onChange={handleChange} renderInput={(params_0): ReactNode => renderInput({
    ...params_0,
    placeholder,
    multiple,
    inputElement: localInputElement
  }, color, helperText, onPaste, onBlur)} renderOption={(optionProps, option_11, state) => renderOption(optionProps, option_11, state, noOptionsTextTemplate, localInputElement?.value ?? '', Boolean(multiple), indeterminateValueSet)} blurOnSelect={!multiple} selectOnFocus={false} clearOnBlur disableCloseOnSelect={multiple} popupIcon={hideOptionsPopup ? null : <ChevronDownIcon />} forcePopupIcon={hideOptionsPopup ? false : undefined} {...hideOptionsPopup ? {
    open: false as const
  } : {}} filterOptions={customFilterOptions} renderTags={renderTags ?? defaultRenderTags} options={options} PopperComponent={createPopperComponent(popperZIndex)} componentsProps={{
    popper: {
      placement: dropdownPlacement
    }
  }} ListboxComponent={ListBoxComponentWithInputValue} groupBy={option_12 => option_12.subCategory ?? ''} renderGroup={renderGroup} sx={{
    '&.MuiAutocomplete-root': {
      width: '326px'
    },
    '&.MuiAutocomplete-fullWidth': {
      width: '100%'
    }
  }} ref={autocompleteRef} color={color} loading={isLoadingOptions} disabled={disabled} loadingText={<Grid display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="body2">{loadingText}</Typography>
          <DotsLoader height={24} width={24} />
        </Grid>} />;
};
export default Autocomplete;
export { Autocomplete };
export type { AutocompleteProps, AutocompleteOptionItem } from './types';